import { useState, useEffect, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { emailRegex } from '../../constants/regex';
import ForgotPasswordModal from './EmployerForgotPasswordModal';
import { SaveState } from '../../types/ForrmType';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { resetPasswordAuthAction } from '../../store/legacy/password-reset';

interface IFormTypes {
  email: string;
}

const ForgotPasswordForm = () => {
  const { emailSentStatus } = useAppSelector((state) => state.passwordReset);

  const dispatch = useAppDispatch();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<IFormTypes>();
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (emailSentStatus === SaveState.FULFILLED) {
      setModalIsOpen(true);
    }
    return () => {
      setModalIsOpen(false);
    };
  }, [emailSentStatus]);

  const [t, i18n] = useTranslation('global');
  const email = register('email', {
    required: true,
    pattern: emailRegex,
  });

  const onSubmit = async (values: IFormTypes) => {
    await dispatch(
      resetPasswordAuthAction({
        email: values.email.toLowerCase(),
        timezoneOffset: new Date().getTimezoneOffset(),
        lang: i18n.language,
      })
    );
  };

  return (
    <form
      data-testid="forgot-password-form"
      className="flex flex-col gap-10"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="w-full flex flex-col gap-1">
        <label htmlFor="email" className="text-spk-mediumContrast text-xs">
          {t('forgotPass.email')}
        </label>
        <IconField iconPosition="left" className="w-full">
          <InputIcon className="pi pi-envelope"> </InputIcon>
          <InputText
            onChange={(e: ChangeEvent) => email.onChange(e)}
            name={email.name}
            onBlur={email.onBlur}
            ref={email.ref}
            type="email"
            className={`w-full ${errors.email && 'border-spk-brightRed'}`}
          />
        </IconField>
        {errors.email && (
          <small data-testid="input-error" className="text-spk-brightRed">
            {t('forgotPass.error')}
          </small>
        )}
      </div>

      <div className="w-fill-available flex gap-4">
        <Button
          severity="success"
          label={t('forgotPass.btn')}
          className="w-full"
          disabled={emailSentStatus === SaveState.PENDING}
        />
      </div>
      {modalIsOpen && <ForgotPasswordModal setModalIsOpen={setModalIsOpen} />}
    </form>
  );
};

export default ForgotPasswordForm;
